/**
 * RoleContractsPage — J7-W2 task 2 (PRD §12.3.10)
 *
 * Card grid of every Role Contract in the org. Supports filtering by status
 * (draft/active/archived/template) and by linked actor. Clones from a
 * template via the existing `from-template/:templateId` endpoint.
 *
 * Surfaces:
 *   - List view at /roros/role-contracts
 *   - "+ New" button → /roros/role-contracts/new
 *   - "Use Template" picker → POST /api/v1/role-contracts/from-template/:id
 *
 * Reuses RorosPage + QueryPage + Card patterns from AiOperatorsPage.
 */

import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { showToast } from '@/components/ui/Toast';
import { ScrollText, Plus, Eye, Pencil, Copy, Trash2 } from 'lucide-react';
import { t } from '@/lib/i18n';
import {
  useRorosRoleContracts,
  useRorosRoleContractTemplates,
  useRorosRoleContractClone,
  useRorosRoleContractArchive,
} from '@/hooks/roros/role-contracts';
import { useRorosActors } from '@/hooks/roros/actors';
import { usePermissions } from '@/hooks/usePermissions';
import type { RorosRoleContractDTO } from '@/services/roros/governance.service';
import type { RorosActorDTO } from '@/types/roros/actors';

function statusBadge(status: string) {
  switch (status) {
    case 'draft':
      return <Badge variant="secondary">{t('Draft')}</Badge>;
    case 'active':
      return <Badge variant="success">{t('Active')}</Badge>;
    case 'archived':
      return <Badge variant="outline">{t('Archived')}</Badge>;
    case 'template':
      return <Badge className="bg-blue-100 text-blue-700">{t('Template')}</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

export function RoleContractsPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [actorFilter, setActorFilter] = useState<string>('all');
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<{ id: string; name: string } | null>(null);

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (statusFilter !== 'all') p.status = statusFilter;
    if (actorFilter !== 'all') p.actorId = actorFilter;
    return Object.keys(p).length ? p : undefined;
  }, [statusFilter, actorFilter]);

  const contractsQuery = useRorosRoleContracts(params);
  const templatesQuery = useRorosRoleContractTemplates();
  const actorsQuery = useRorosActors();
  const cloneMutation = useRorosRoleContractClone();
  const archiveMutation = useRorosRoleContractArchive();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('roros.role-contracts.manage');

  const actorMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of (actorsQuery.data as RorosActorDTO[] | undefined) ?? []) {
      map.set(a.id, a.name);
    }
    return map;
  }, [actorsQuery.data]);

  const handleArchiveConfirm = () => {
    if (!archiveTarget) return;
    archiveMutation.mutate(archiveTarget.id, {
      onSuccess: () => {
        showToast.success(t('Role contract deleted'));
        setArchiveTarget(null);
      },
      onError: (err) =>
        showToast.error(err instanceof Error ? err.message : t('Failed to delete role contract')),
    });
  };

  const handleClone = (templateId: string) => {
    cloneMutation.mutate(
      { templateId, payload: {} },
      {
        onSuccess: (created) => {
          setShowTemplatePicker(false);
          showToast.success(t('Role Contract created from template'));
          navigate(`/roros/role-contracts/${created.id}/edit`);
        },
        onError: (err) =>
          showToast.error(err instanceof Error ? err.message : t('Failed to clone template')),
      },
    );
  };

  return (
    <RorosPage
      title={t('Role Contracts')}
      icon={<ScrollText className="h-5 w-5" />}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowTemplatePicker(true)}
            leftIcon={<Copy className="h-4 w-4" />}
            data-testid="role-contracts-template-button"
          >
            {t('Use Template')}
          </Button>
          <Button
            onClick={() => navigate('/roros/role-contracts/new')}
            leftIcon={<Plus className="h-4 w-4" />}
            data-testid="role-contracts-new-button"
          >
            {t('New Role Contract')}
          </Button>
        </div>
      }
    >
      <div className="mt-4 flex flex-wrap gap-3">
        <Select
          label={t('Status')}
          value={statusFilter}
          onValueChange={setStatusFilter}
          options={[
            { value: 'all', label: t('All statuses') },
            { value: 'draft', label: t('Draft') },
            { value: 'active', label: t('Active') },
            { value: 'archived', label: t('Archived') },
            { value: 'template', label: t('Template') },
          ]}
        />
        <Select
          label={t('Actor')}
          value={actorFilter}
          onValueChange={setActorFilter}
          options={[
            { value: 'all', label: t('All actors') },
            ...(((actorsQuery.data as RorosActorDTO[] | undefined) ?? []).map((a) => ({
              value: a.id,
              label: a.name,
            }))),
          ]}
        />
      </div>

      <div className="mt-6">
        <QueryPage
          query={contractsQuery}
          emptyMessage={t('No Role Contracts yet')}
          emptyAction={
            <Button onClick={() => navigate('/roros/role-contracts/new')} leftIcon={<Plus className="h-4 w-4" />}>
              {t('Create your first Role Contract')}
            </Button>
          }
        >
          {(contracts) => {
            const list = contracts as RorosRoleContractDTO[];
            if (list.length === 0) {
              return (
                <div className="text-center text-sm text-muted-foreground py-8">
                  {t('No Role Contracts match the current filters.')}
                </div>
              );
            }
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr">
                {list.map((c) => (
                  <Card
                    key={c.id}
                    padding="none"
                    className="h-full hover:shadow-md transition-shadow"
                    data-testid="role-contract-card"
                  >
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <Link
                          to={`/roros/role-contracts/${c.id}`}
                          className="font-medium text-base hover:underline truncate flex-1"
                        >
                          {c.name}
                        </Link>
                        {statusBadge(c.status)}
                      </div>
                      {c.purposeScope && (
                        <p className="text-xs text-muted-foreground line-clamp-3">
                          {c.purposeScope}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        {c.actorId && (
                          <Badge variant="outline" className="text-xs">
                            {actorMap.get(c.actorId) ?? c.actorId.slice(0, 8)}
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-xs">
                          v{c.version}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-end gap-2 mt-auto pt-4 border-t">
                        <Link to={`/roros/role-contracts/${c.id}`}>
                          <Button size="sm" variant="outline">
                            <Eye className="h-3.5 w-3.5 mr-1" />
                            {t('View')}
                          </Button>
                        </Link>
                        <Link to={`/roros/role-contracts/${c.id}/edit`}>
                          <Button size="sm" variant="outline">
                            <Pencil className="h-3.5 w-3.5 mr-1" />
                            {t('Edit')}
                          </Button>
                        </Link>
                        {canManage && c.status !== 'archived' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setArchiveTarget({ id: c.id, name: c.name })}
                            disabled={archiveMutation.isPending}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            {t('Delete')}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            );
          }}
        </QueryPage>
      </div>

      <Modal
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        title={t('Use a Role Contract Template')}
        size="md"
      >
        {templatesQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">{t('Loading...')}</p>
        ) : templatesQuery.data && templatesQuery.data.length > 0 ? (
          <div className="space-y-2">
            {(templatesQuery.data as RorosRoleContractDTO[]).map((tpl) => (
              <Button
                key={tpl.id}
                type="button"
                variant="outline"
                onClick={() => handleClone(tpl.id)}
                disabled={cloneMutation.isPending}
                className="w-full flex items-start justify-between gap-3 p-3 rounded-lg text-left h-auto"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{tpl.name}</div>
                  {tpl.purposeScope && (
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{tpl.purposeScope}</p>
                  )}
                </div>
                <Copy className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
              </Button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-4">
            {t('No templates available yet. Create a contract and mark it as Template to seed one.')}
          </p>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchiveConfirm}
        title={t('Delete this role contract?')}
        description={archiveTarget ? `"${archiveTarget.name}"` : ''}
        confirmText={t('Delete')}
        variant="danger"
        isLoading={archiveMutation.isPending}
      />
    </RorosPage>
  );
}
